import {computed, ref} from 'vue';

import settings from '@/config/settings';

import type {IPagination, TId} from '@/assets/core/assets/api/types';
import type {IFeature} from '@/api/types/layer/feature';
import type {TLogicItem} from '@/api/types/logic';

import {activeStatesState} from '@/services/state/active_states';

import useRequestHook from '@/assets/core/assets/hooks/requestHook';

import {serializeFromObjectToQueryString} from '@/assets/core/assets/helpers/serialize';
import {generatePolygonToAllMap} from '@/assets/leaflet/assets/helpers/generate';
import {
  parseFeatureFromIFeature,
  parseWktFromFeature,
} from '@/assets/leaflet/assets/helpers/parse';
import {featureCache} from '@/services/cache/feature';
import {layerCache} from '@/services/cache/layer';
import type {Feature, MultiPolygon, Polygon} from '@turf/turf';
import {difference, intersect, union} from '@turf/turf';

export interface ILFeatureLoaderCacheItem {
  intersects: Feature<Polygon | MultiPolygon>;
}

export interface IRequestQueueItem {
  layerId: TId;
  intersects: Feature<Polygon | MultiPolygon>;
  exclude?: Feature<Polygon | MultiPolygon>;
  filters?: TLogicItem[];
  page: number;
  callback: (data?: IPagination<IFeature>) => void;
}

export interface ILFeatureLoaderQueueItem {
  layerId: TId;
  features: Feature[];
}

const pageSize = 5000;
const parallelRequestCount = 3;

const baseUrl = `${settings.SERVICES.BACK.getUrl()}/layers/{id}/features`;

export const useLFeatureLoaderHook = () => {
  const request = useRequestHook();

  const queueAdd = ref<ILFeatureLoaderQueueItem[]>([]);
  const queueRequests = ref<IRequestQueueItem[]>([]);
  const queueRequestsExecutorsCount = ref<number>(0);

  const cache = ref<Record<TId, ILFeatureLoaderCacheItem>>({});

  const getQueueAdd = computed(() => [...queueAdd.value]);
  const getCache = computed(() => cache.value);

  const excludeFeatureIds = new Set<TId>();

  const pushQueueAdd = (feature: ILFeatureLoaderQueueItem) =>
    queueAdd.value.push(feature);

  const shiftQueueAdd = () =>
    queueAdd.value.shift() as ILFeatureLoaderQueueItem;

  async function doRequest(
    item: IRequestQueueItem,
  ): Promise<IPagination<IFeature>> {
    const url =
      baseUrl.replace('{id}', item.layerId) +
      '?' +
      serializeFromObjectToQueryString({
        intersects: parseWktFromFeature(item.intersects),
        exclude: parseWktFromFeature(item.exclude),
        size: pageSize,
        filters: item.filters?.map((val) => JSON.stringify(val)),
        page: item.page,
      });

    return await request.get(url);
  }

  async function executeRequestQueueItem() {
    if (queueRequests.value.length === 0) {
      queueRequestsExecutorsCount.value -= 1;
      return;
    }

    const item = queueRequests.value.shift() as IRequestQueueItem;
    const data = await doRequest(item);

    item.callback(data);

    const dataItems = data.items
      .filter((item) => !excludeFeatureIds.has(item.id))
      .map(parseFeatureFromIFeature);

    if (!featureCache.cache.value[item.layerId])
      featureCache.cache.value[item.layerId] = [...dataItems];
    else featureCache.cache.value[item.layerId].push(...dataItems);

    pushQueueAdd({layerId: item.layerId, features: dataItems});

    if (queueRequestsExecutorsCount.value < parallelRequestCount)
      startRequestQueue();
    executeRequestQueueItem();
  }

  async function startRequestQueue() {
    const promises = new Array(
      parallelRequestCount - queueRequestsExecutorsCount.value,
    )
      .fill(1)
      .map(async () => {
        queueRequestsExecutorsCount.value += 1;
        return await executeRequestQueueItem();
      });

    await Promise.all(promises);
  }

  const load = async (
    layerId: TId,
    bounds?: Feature<Polygon>,
    exclude?: Feature<Polygon | MultiPolygon>,
    filters?: TLogicItem[],
  ) => {
    if (!bounds) bounds = generatePolygonToAllMap();
    else {
      const localBounds = intersect(bounds, generatePolygonToAllMap());
      if (localBounds) bounds = localBounds;
      else return;
    }

    const currentCache = cache.value[layerId];
    if (!currentCache) cache.value[layerId] = {intersects: bounds};
    else {
      if (!exclude && currentCache.intersects)
        exclude = JSON.parse(JSON.stringify(currentCache.intersects));

      const dif = difference(bounds, currentCache.intersects);
      if (dif) {
        currentCache.intersects =
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          union(currentCache.intersects, bounds) ?? generatePolygonToAllMap();
      } else return;
    }

    let queueSize: number = 1;
    let resolvePromise: null | ((value?: never | PromiseLike<never>) => void) =
      null;
    const intersects = bounds;

    addLoadingLayer(layerId);

    queueRequests.value.push({
      layerId: layerId,
      intersects: intersects,
      exclude: exclude,
      filters: filters,
      page: 1,
      callback(data) {
        queueSize -= 1;
        const total = data?.total ?? 0;

        if (total <= pageSize) {
          if (!!resolvePromise) resolvePromise();
          return;
        }
        const pageCount = Math.ceil(total / pageSize);

        new Array(pageCount - 1).fill(1).forEach((_, index) => {
          queueSize += 1;
          queueRequests.value.push({
            layerId: layerId,
            intersects: intersects,
            exclude: exclude,
            filters: filters,
            page: index + 2,
            callback() {
              queueSize -= 1;
              if (!queueSize && !!resolvePromise) resolvePromise();
            },
          });
        });
      },
    });

    if (queueRequestsExecutorsCount.value < parallelRequestCount)
      startRequestQueue();
    if (queueSize > 0)
      await new Promise((resolve) => (resolvePromise = resolve));

    deleteLoadingLayer(layerId);
  };

  const addLoadingLayer = (id: TId) => {
    activeStatesState.addActiveState({
      id: id,
      label: () => `Слой "${layerCache.getCache.value[id].name}" загружается`,
    });
  };

  const deleteLoadingLayer = (id: TId) =>
    activeStatesState.removeActiveState(id);

  const clearQueueAdd = () => (queueAdd.value = []);
  const clearCache = (id: TId) => {
    if (!id) return;
    delete cache.value[id];
  };

  const addToExclude = (featureId: TId) => {
    excludeFeatureIds.add(featureId);
  };

  return {
    load,

    getQueueAdd,
    pushQueueAdd,
    shiftQueueAdd,
    clearQueueAdd,
    getCache,
    clearCache,
    addToExclude,
  };
};

export const lFeatureLoader = useLFeatureLoaderHook();
